import { FetchPolicy, useApolloClient } from '@apollo/client';
import { observer } from 'mobx-react-lite';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list/lib/flights';
import { useManagedMutation, useManagedQuery } from 'owa-apollo-hooks';
import type { MailboxInfo } from 'owa-client-ids';
import { lazyMoveFolder } from 'owa-folder-movefolder';
import { MoveFolderDocument } from 'owa-folder-movefolder/lib/graphql/__generated__/MoveFolderMutation.interface';
import { onFolderHierarchyResponse, OnFolderHierarchyResponseFragment } from 'owa-folders';
import {
    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID,
    PRIMARY_FOLDERS_TREE_TYPE,
    PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
} from 'owa-folders-constants';
import { shouldShowFolder } from 'owa-folders-data-utils';
import loc from 'owa-localize';
import viewStateStore from 'owa-mail-folder-store/lib/store/store';
import { MailFolderRoot } from 'owa-mail-folder-view';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import * as React from 'react';
import type { FolderHierarchyConfigurationQueryQuery } from '../graphql/__generated__/FolderHierarchyConfigurationQuery.interface';
import { ToggleFolderTreeCollapsedDocument } from '../graphql/__generated__/ToggleFolderTreeCollapsedStateMutation.interface';
import { toggleFolderTreeCollapsed } from '../operations/toggleFolderTreeCollapsed';
import { MailFolderContextMenu } from './lazyFolderComponents';
import { MailFolderListWrapper } from './MailFolderListWrapper';
import { Folders } from './PrimaryMailFolderTreeContainer.locstring.json';
import { MailFolderTreeContainerDocument } from './__generated__/MailFolderTreeContainerQuery.interface';
import { PrimaryMailFolderHierarchyDocument } from './__generated__/PrimaryMailFolderHierarchyQuery.interface';

export interface PrimaryMailFolderTreeContainerProps {
    showRootNode: boolean;
    className: string;
    setSize?: number;
    positionInSet?: number;
    userConfiguration: FolderHierarchyConfigurationQueryQuery['userConfiguration'];
    isShadowMailbox: boolean;
    mailboxInfo: MailboxInfo;
}

/**
 * Primary mailbox container
 */
export default observer(function PrimaryMailFolderTreeContainer(
    props: PrimaryMailFolderTreeContainerProps
) {
    const [initialLoading, setInitialLoading] = React.useState<boolean>(true);
    const [moveFolderMutation] = useManagedMutation(MoveFolderDocument);
    const [toggleFolderTreeCollapsedMutation] = useManagedMutation(
        ToggleFolderTreeCollapsedDocument
    );

    const userConfiguration = props.userConfiguration;
    const mailboxInfo = props.mailboxInfo;
    const isExpanded = !userConfiguration.UserOptions.IsMailRootFolderTreeCollapsed;

    // Primary mailbox root click handler
    const onRootNodeChevronClicked = () => {
        toggleFolderTreeCollapsed(toggleFolderTreeCollapsedMutation, !isExpanded);
    };

    const client = useApolloClient();

    const fetchData = async (offset: number, fetchPolicy: FetchPolicy) => {
        try {
            const data = await client.query({
                query: PrimaryMailFolderHierarchyDocument,
                variables: {
                    baseFolderDistinguishedIds: [PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID],
                    offset: offset,
                    mailboxInfo: mailboxInfo,
                    shouldShowJunkEmailFolder: shouldShowFolder('junkemail'),
                    shouldShowNotesFolder: shouldShowFolder('notes'),
                },
                fetchPolicy: fetchPolicy,
            });

            setInitialLoading(false);
            if (data.data?.folderHierarchy) {
                syncSatchel(data.data.folderHierarchy);
            }
        } catch (e) {
            setInitialLoading(false);
        }
    };

    React.useEffect(() => {
        if (!props.showRootNode && !isExpanded) {
            // If the 'Folders' root node is hidden, and the root node is collapsed
            // we need to explicitly expand the root node for it to be accessible
            toggleFolderTreeCollapsed(toggleFolderTreeCollapsedMutation, !isExpanded);
        }

        // Pre-fetch data once on initialization
        fetchData(0 /* offset */, 'cache-first');
    }, []);

    const moveFolder = (
        destinationFolderId: string,
        destinationFolderMailboxInfo: MailboxInfo,
        sourceFolderId: string,
        sourceFolderMailboxInfo: MailboxInfo,
        sourceFolderParentFolderId: string,
        sourceFolderDisplayName: string
    ) => {
        lazyMoveFolder.importAndExecute(
            moveFolderMutation,
            destinationFolderId /*destinationFolderId */,
            destinationFolderMailboxInfo /* destinationFolderMailboxInfo */,
            sourceFolderId /* sourceFolderId */,
            sourceFolderMailboxInfo /* sourceFolderMailboxInfo */,
            sourceFolderParentFolderId /* parentFolderId */,
            sourceFolderDisplayName
        );
    };

    const rootDisplayName = isMonarchMultipleAccountsEnabled()
        ? userConfiguration.SessionSettings?.UserEmailAddress || loc(Folders)
        : loc(Folders);

    const isExplicitLogon = userConfiguration.SessionSettings?.IsExplicitLogon;

    const syncSatchel = (folderHierarchy: OnFolderHierarchyResponseFragment) => {
        onFolderHierarchyResponse(
            folderHierarchy.RootFolder,
            folderHierarchy.Folders,
            PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
            props.mailboxInfo.userIdentity,
            props.userConfiguration.SessionSettings?.UserDisplayName,
            PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
            props.mailboxInfo.userIdentity,
            folderHierarchy.IncludesLastItemInRange,
            folderHierarchy.offset
        );
    };

    /**
     * Query FolderHierarchy root folder and meta-data
     */
    const { loading, data } = useManagedQuery(MailFolderTreeContainerDocument, {
        variables: {
            baseFolderDistinguishedIds: [PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID],
            offset: 0,
            mailboxInfo: props.mailboxInfo,
        },
        fetchPolicy: 'cache-only',
        nextFetchPolicy: 'cache-only',
    });

    const rootFolder = data?.folderHierarchy.RootFolder;
    const shouldShowLoadingSpinner = !data && (initialLoading || loading);
    const hasMoreData = data && !data.folderHierarchy.IncludesLastItemInRange;
    const offset = data?.folderHierarchy.offset || 0;

    const shouldShowContextMenu = React.useCallback((): boolean => {
        const { contextMenuState } = viewStateStore;

        // Render the context menu if it is set to be visible and for different folder tree, we render a new context menu
        if (!contextMenuState || contextMenuState.treeType != PRIMARY_FOLDERS_TREE_TYPE) {
            return false;
        }
        return true;
    }, []);

    const renderContextMenu = (): JSX.Element => {
        return (
            <MailFolderContextMenu
                shouldHideToggleFavorite={isExplicitLogon}
                treeType={PRIMARY_FOLDERS_TREE_TYPE}
                isShadowMailbox={props.isShadowMailbox}
                mailboxInfo={mailboxInfo}
                deletedItemsDistinguishedFolderId={PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID}
                primaryMailboxInfo={mailboxInfo}
            />
        );
    };

    return (
        <>
            {/* Render tree root */}
            {props.showRootNode && (
                <MailFolderRoot
                    displayName={rootDisplayName}
                    onRootNodeChevronClickedCallback={onRootNodeChevronClicked}
                    positionInSet={props.positionInSet}
                    rootFolder={rootFolder}
                    rootNodeId={PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID}
                    setSize={props.setSize}
                    shouldBeDroppable={true}
                    treeType={PRIMARY_FOLDERS_TREE_TYPE}
                    isRootExpanded={isExpanded}
                    mailboxInfo={mailboxInfo}
                    shouldShowRootNodeContextMenu={!isConsumer()}
                    moveFolder={moveFolder}
                />
            )}
            {/* Render rest of the tree if root is expanded */}
            {isExpanded && (
                <MailFolderListWrapper
                    className={props.className}
                    key="primarymailboxtree"
                    treeType={PRIMARY_FOLDERS_TREE_TYPE}
                    isFavoritesSupported={!isExplicitLogon} // Favorites not supported for explicit logon scenarios
                    hasMoreData={hasMoreData}
                    shouldShowLoadingSpinner={shouldShowLoadingSpinner}
                    rootFolder={rootFolder}
                    rootNodeId={PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID}
                    mailboxInfo={mailboxInfo}
                    moveFolder={moveFolder}
                    offset={offset}
                    fetchMore={fetchData}
                />
            )}
            {/* Render Context Menu */}
            {shouldShowContextMenu() && renderContextMenu()}
        </>
    );
});
